import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';
import { DependencyTypeSchema, ReminderSchema, ScheduleColourSchema } from './schedule.ts';

/**
 * A phase in a template, identified by its position.
 *
 * Templates hold no ids. They are applied over and over, to jobs that do not
 * exist yet, so anything they reference has to be relative — a stored id
 * would point at one job's phase and be meaningless on the next.
 */
export const TemplatePhaseSchema = z.object({
  name: z.string().min(1).max(120),
  colour: ScheduleColourSchema,
});
export type TemplatePhase = z.infer<typeof TemplatePhaseSchema>;

export const TemplatePredecessorSchema = z.object({
  /** Index into the template's own items, not an id. */
  itemIndex: z.number().int().min(0),
  type: DependencyTypeSchema,
  lagDays: z.number().int().min(-60).max(365),
});

/**
 * One item in a template.
 *
 * `startOffsetWorkDays` is counted in **working days** from the date the
 * template is applied, like every other duration in this module. A
 * construction template means "drywall starts ten working days after we
 * begin", and counting in calendar days would stretch or squeeze that
 * depending on which weekday the job happens to start.
 */
export const TemplateItemSchema = z.object({
  title: z.string().min(1).max(200),
  colour: ScheduleColourSchema,
  /** Index into the template's phases, or absent for an ungrouped item. */
  phaseIndex: z.number().int().min(0).optional(),
  startOffsetWorkDays: z.number().int().min(0).max(2000),
  workDays: z
    .number()
    .int()
    .min(1)
    .max(500, 'A template item cannot run longer than 500 work days'),
  reminder: ReminderSchema.default('none'),
  predecessors: z.array(TemplatePredecessorSchema).default([]),
  /**
   * The **kinds** of machine this work needs, not the units (SCRUM-210).
   *
   * A template is applied to jobs that do not exist yet, so it cannot
   * name `EX-04` — one company's unit name is meaningless to another, and
   * even within a company the right machine depends on what is free that
   * week. Applying the template turns each type into an actual booking;
   * see `pickPlant` for how, and for the maintainer's rule that it picks a
   * free one, or a busy one with a warning, but never nothing.
   */
  equipmentTypeIds: z.array(IdSchema).default([]),
  /**
   * Materials this work needs, from the company catalog (SCRUM-210).
   *
   * **Catalog ids, with a quantity, and no price.** The catalog is the
   * company's own and its prices move; a template that froze a price
   * would quietly estimate a job at last year's cost. The quantity is
   * what a template can honestly carry — "this activity takes forty
   * linear feet of pipe" — and what that costs is read from the catalog
   * on the day it is applied.
   */
  materials: z
    .array(
      z.object({
        catalogItemId: IdSchema,
        /** Thousandths, like every quantity in this product. */
        quantityThousandths: z.number().int().min(0).default(1_000),
      }),
    )
    .max(200)
    .default([]),
});
export type TemplateItem = z.infer<typeof TemplateItemSchema>;

/**
 * A reusable schedule skeleton, held on the company rather than a job.
 *
 * The point of a template is that the same sequence of work gets built the
 * same way on every job, so it belongs to the company that works that way.
 */
export const ScheduleTemplateSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  phases: z.array(TemplatePhaseSchema).max(100).default([]),
  items: z.array(TemplateItemSchema).max(500, 'A template holds at most 500 items').default([]),
});
export type ScheduleTemplate = z.infer<typeof ScheduleTemplateSchema>;
